import { useCallback, useEffect, useMemo } from 'react';
import Page from '../../../components/page/Page';
import Header from '../../../components/meCourses/Header';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import { alpha, Avatar, Box, Paper, Typography } from '@mui/material';
import BrandLoader from '../../../components/BrandLoader';
import MeCourseCard from '../../../components/meCourses/MeCourseCard';
import { useNavigationCore } from '../../../../core/router/navigation';
import useNewNavigation from '../../../../core/hooks/useNewNavigation';
import CreateDialog from '../../../components/courses/CreateDialog';
import { Course } from '../../../../core/interfaces/course.interface';
import { useTranslation } from 'react-i18next';
import { useMeCore } from '../../../../core/modules/me';
import { useCourseCore } from '../../../../core/modules/course';
import mixpanel from 'mixpanel-browser';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WorkspacePremiumOutlinedIcon from '@mui/icons-material/WorkspacePremiumOutlined';

const EmptyCoursesState = () => {
  const { t } = useTranslation();

  const highlights = [
    {
      icon: <MenuBookOutlinedIcon fontSize="small" />,
      label: t('courses.empty.highlight.classes'),
    },
    {
      icon: <TrendingUpIcon fontSize="small" />,
      label: t('courses.empty.highlight.progress'),
    },
    {
      icon: <WorkspacePremiumOutlinedIcon fontSize="small" />,
      label: t('courses.empty.highlight.resources'),
    },
  ];

  return (
    <Box className="flex flex-1 items-center justify-center h-full px-24 py-40">
      <Paper
        elevation={0}
        sx={(theme) => ({
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          maxWidth: 760,
          borderRadius: 4,
          px: { xs: 3, sm: 6 },
          py: { xs: 4, sm: 6 },
          textAlign: 'center',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${theme.palette.background.paper} 48%, ${alpha(theme.palette.secondary.main, 0.14)} 100%)`,
          boxShadow: '0 24px 80px -48px rgba(15, 23, 42, 0.65)',
        })}
      >
        <Box
          sx={(theme) => ({
            position: 'absolute',
            top: -90,
            right: -70,
            width: 220,
            height: 220,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.secondary.main, 0.16),
          })}
        />
        <Box
          sx={(theme) => ({
            position: 'absolute',
            bottom: -80,
            left: -60,
            width: 180,
            height: 180,
            borderRadius: '50%',
            backgroundColor: alpha(theme.palette.primary.main, 0.12),
          })}
        />

        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Avatar
            sx={(theme) => ({
              width: 88,
              height: 88,
              mx: 'auto',
              mb: 3,
              color: theme.palette.common.white,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              boxShadow: `0 18px 38px -18px ${alpha(theme.palette.primary.main, 0.8)}`,
            })}
          >
            <AssignmentTurnedInOutlinedIcon sx={{ fontSize: 42 }} />
          </Avatar>

          <Typography
            variant="overline"
            sx={{ fontWeight: 800, letterSpacing: 1.2 }}
            color="primary"
          >
            {t('sidebar.link.mycourses')}
          </Typography>

          <Typography
            className="mt-8"
            variant="h4"
            sx={{ fontWeight: 800, lineHeight: 1.15 }}
          >
            {t('courses.empty.title')}
          </Typography>

          <Typography
            className="mt-16"
            color="text.secondary"
            sx={{ maxWidth: 560, mx: 'auto', fontSize: { xs: 15, sm: 16 }, lineHeight: 1.7 }}
          >
            {t('courses.empty.description')}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 2,
              mt: 4,
            }}
          >
            {highlights.map((highlight) => (
              <Box
                key={highlight.label}
                sx={(theme) => ({
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  minHeight: 54,
                  px: 2,
                  py: 1.5,
                  borderRadius: 3,
                  color: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.common.white, 0.72),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  fontWeight: 700,
                })}
              >
                {highlight.icon}
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {highlight.label}
                </Typography>
              </Box>
            ))}
          </Box>

          <Typography className="mt-32" variant="body2" color="text.secondary">
            {t('courses.empty.help')}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

const MeCourses = () => {
  const { user, studentCourses, profesorCourses, getMeCourses } = useMeCore();
  const { courses, getCourses, setCurrentClassId } = useCourseCore();
  const { goToMeCourse, goToInicio } = useNavigationCore();
  const { hideNavbar } = useNewNavigation();

  // Con la navegación nueva "Mis cursos" vive dentro del Inicio
  useEffect(() => {
    if (hideNavbar) {
      goToInicio();
    }
  }, [goToInicio, hideNavbar]);

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  useEffect(() => {
    if (!user) {
      return;
    }

    if (user.userRole.admin) {
      getCourses({});
    } else {
      getMeCourses();
    }
  }, [getCourses, getMeCourses, user]);

  const onSelecting = useCallback((course: Course) => {
    if (process.env.REACT_APP_MIXPANEL_TOKEN) {
      mixpanel.track('Selecting course', {
        'id': course.id,
        'name': course.name,
      });
    }
    setCurrentClassId('');
    goToMeCourse(course.id);
  }, [goToMeCourse, setCurrentClassId]);

  const userCourses = useMemo(() => {
    if (!user) return null;
    if (user.userRole.admin) return courses;
    if (user.userRole.student) return studentCourses.map((course) => course.course);
    if (user.userRole.professor) return profesorCourses;

    return [];
  }, [courses, profesorCourses, studentCourses, user]);

  return (
    <>
      <Page
        content={
          userCourses === null ? (
            <BrandLoader message="Cargando cursos..." />
          ) : !userCourses.length ? (
            <EmptyCoursesState />
          ) : (
            <div className="w-full">
              <Header />
              <div className="w-full my-16 sm:col-span-3 px-24 md:px-32">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-24 w-full min-w-0 items-start">
                  {userCourses.map((course: Course) => (
                    <MeCourseCard
                      key={course.id}
                      course={course}
                      onSelect={onSelecting}
                    />
                  ))}
                </div>
              </div>
            </div>
          )
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <CreateDialog />
    </>
  );
};

export default MeCourses;
